import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, TrendingUp, Award, Clock, Heart } from "lucide-react";
import { useLeaderboard, LeaderboardEntry } from "@/hooks/useLeaderboard";
import { MedalRing, RankBadge } from "@/components/shared/BadgeDisplay";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/admin/leaderboard/")({
  component: LeaderboardPage,
});

type Period = "all" | "week" | "month";

const PERIOD_LABELS: Record<Period, string> = {
  all: "All Time",
  week: "This Week",
  month: "This Month",
};

function TopThreeHighlight({ entry, rank }: { entry: LeaderboardEntry; rank: number }) {
  if (![1, 2, 3].includes(rank)) return null;

  return (
    <div className="flex h-full flex-col items-center gap-4 rounded-md border border-border bg-card p-5 transition-colors hover:border-hairline-strong">
      <MedalRing rank={rank} />

      <div className="w-full min-w-0 text-center">
        <p className="truncate text-[15px] leading-6 font-medium text-foreground">
          {entry.student_name}
        </p>
        <p className="truncate text-caption text-muted-foreground">{entry.email}</p>
      </div>

      <dl className="flex items-center justify-center gap-5">
        <div className="text-center">
          <dd className="inline-flex items-center gap-1 text-base text-foreground tabular-nums">
            <Clock className="size-3 text-muted-foreground" aria-hidden />
            {entry.total_hours?.toFixed(1) ?? "0"}
          </dd>
          <dt className="eyebrow mt-0.5">Hours</dt>
        </div>
        <div className="h-8 w-px bg-border" aria-hidden />
        <div className="text-center">
          <dd className="inline-flex items-center gap-1 text-base text-foreground tabular-nums">
            <Heart className="size-3 text-success" aria-hidden />
            {entry.tips_received ?? 0}
          </dd>
          <dt className="eyebrow mt-0.5">Tips</dt>
        </div>
      </dl>
    </div>
  );
}

function LeaderboardPage() {
  const [period, setPeriod] = useState<Period>("all");
  const { data: entries, isLoading } = useLeaderboard(period);
  const leaderboardEntries = Array.isArray(entries) ? entries : [];

  const topThree = leaderboardEntries.filter((e) => e.rank <= 3);
  const rest = leaderboardEntries.filter((e) => e.rank > 3);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leaderboard"
        subtitle="Students ranked by total hours worked, tips received, and badges earned."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Leaderboard" }]}
      />

      <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
        <TabsList variant="segmented" aria-label="Ranking period">
          <TabsTrigger value="all">
            <Award />
            {PERIOD_LABELS.all}
          </TabsTrigger>
          <TabsTrigger value="week">
            <TrendingUp />
            {PERIOD_LABELS.week}
          </TabsTrigger>
          <TabsTrigger value="month">
            <TrendingUp />
            {PERIOD_LABELS.month}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div aria-label="Loading leaderboard…">
          <TableSkeleton rows={8} columns={6} />
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={period}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="space-y-6"
          >
            {topThree.length > 0 && (
              <div className="grid gap-3 md:grid-cols-3 md:gap-4">
                {([1, 2, 3] as const).map((rank) => {
                  const entry = topThree.find((e) => e.rank === rank);
                  if (!entry) return null;
                  return <TopThreeHighlight key={rank} entry={entry} rank={rank} />;
                })}
              </div>
            )}

            <SectionCard title="Full ranking" noPadding>
              {leaderboardEntries.length === 0 ? (
                <EmptyState
                  bare
                  icon={<Trophy />}
                  title="No leaderboard data available yet"
                  description="Rankings will appear once students start earning points."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="pl-4 md:pl-5">Rank</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Tips</TableHead>
                      <TableHead className="pr-4 text-center md:pr-5">Badges</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(rest.length > 0 ? rest : leaderboardEntries).map((entry) => {
                      if (entry.rank <= 3) return null;
                      return (
                        <TableRow key={entry.student_id}>
                          <TableCell className="pl-4 md:pl-5">
                            <RankBadge rank={entry.rank} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="size-8 rounded-full">
                                <AvatarFallback className="rounded-full text-caption">
                                  {entry.student_name?.charAt(0)?.toUpperCase() ?? "?"}
                                </AvatarFallback>
                              </Avatar>
                              <span className="whitespace-nowrap font-medium text-foreground">
                                {entry.student_name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{entry.email}</TableCell>
                          <TableCell className="text-right">
                            <span className="inline-flex items-center gap-1 font-mono text-body tabular-nums">
                              <Clock className="size-3 text-muted-foreground" aria-hidden />
                              {`${entry.total_hours?.toFixed(1) ?? 0}h`}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="inline-flex items-center gap-1 font-mono text-body tabular-nums">
                              <Heart className="size-3 text-success" aria-hidden />
                              {entry.tips_received ?? 0}
                            </span>
                          </TableCell>
                          <TableCell className="pr-4 text-center md:pr-5">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="tabular-nums" tabIndex={0}>
                                  {entry.badges_count ?? 0}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>{`${entry.badges_count ?? 0} badges earned`}</TooltipContent>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </SectionCard>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
